import { toPng } from 'html-to-image';
import { StickyState, TextLabelState } from '../../../api/Requests';
import { stickyDisplaySize } from '../../../utils/canvasUtils';

const EXPORT_PADDING = 40; // px added on every side of the bounding box

export interface BoundingBox {
  x: number;
  y: number;
  width: number;
  height: number;
}

/** Board-space rectangle that should be included in the export (e.g. template bounds). */
export interface BoardRect {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}

/**
 * DOM dimensions for a text label element measured at export time.
 * Both width and height are fit-content values that cannot be derived from state.
 */
export type LabelDomDims = Map<string, { w: number; h: number }>;

/**
 * Optional template to include in the export.
 * The element is the TemplateLayer root div; boardRect is the template's full
 * bounding box in board-space coordinates.
 * isDarkMode controls whether the dark-mode filter (invert) is preserved so
 * the exported template visually matches the active board theme.
 */
export interface TemplateExportInfo {
  el: HTMLElement;
  boardRect: BoardRect;
  isDarkMode: boolean;
}

/**
 * Compute the axis-aligned bounding box that contains all supplied items.
 * Label dimensions must come from DOM measurements because text labels use
 * `height: fit-content` and store no meaningful width/height in state.
 * When templateBoardRect is provided the bounding box is expanded to include
 * the full template area (used for full-board exports).
 * Returns null when there is nothing to export.
 */
export function computeBoundingBox(
  stickies: StickyState[],
  labels: TextLabelState[],
  labelDims: LabelDomDims,
  templateBoardRect?: BoardRect,
): BoundingBox | null {
  const hasContent = stickies.length > 0 || labels.length > 0 || !!templateBoardRect;
  if (!hasContent) return null;

  // Seed with template bounds when provided so the box always covers the template.
  let minX = templateBoardRect ? templateBoardRect.minX : Infinity;
  let minY = templateBoardRect ? templateBoardRect.minY : Infinity;
  let maxX = templateBoardRect ? templateBoardRect.maxX : -Infinity;
  let maxY = templateBoardRect ? templateBoardRect.maxY : -Infinity;

  for (const s of stickies) {
    const { w, h } = stickyDisplaySize(s.width, s.height, s.shape);
    minX = Math.min(minX, s.x);
    minY = Math.min(minY, s.y);
    maxX = Math.max(maxX, s.x + w);
    maxY = Math.max(maxY, s.y + h);
  }

  for (const l of labels) {
    const dims = labelDims.get(l.id);
    // Fall back to sensible defaults when DOM hasn't been measured yet.
    const lw = dims ? dims.w : 200;
    const lh = dims ? dims.h : 40;
    minX = Math.min(minX, l.x);
    minY = Math.min(minY, l.y);
    maxX = Math.max(maxX, l.x + lw);
    maxY = Math.max(maxY, l.y + lh);
  }

  return {
    x:      minX - EXPORT_PADDING,
    y:      minY - EXPORT_PADDING,
    width:  maxX - minX + EXPORT_PADDING * 2,
    height: maxY - minY + EXPORT_PADDING * 2,
  };
}

interface CloneOptions {
  /**
   * When true, background and box-shadow are cleared on the root and all
   * descendants so only the text itself is visible — used for text labels,
   * which appear as floating text with no box on the canvas.
   */
  clearBackground?: boolean;
  /** Overrides the default z-index of 1. */
  zIndex?: string;
}

/**
 * Clone an element, reposition it relative to the export bounding box, and
 * append it to the container. html-to-image reads getComputedStyle on the live
 * DOM nodes in the container during serialisation, so styles are preserved.
 */
function appendClone(
  el: HTMLElement,
  itemX: number,
  itemY: number,
  bounds: BoundingBox,
  container: HTMLDivElement,
  options: CloneOptions = {},
): void {
  const clone = el.cloneNode(true) as HTMLElement;
  clone.style.position  = 'absolute';
  clone.style.left      = `${itemX - bounds.x}px`;
  clone.style.top       = `${itemY - bounds.y}px`;
  clone.style.transform = 'none';
  clone.style.zIndex    = options.zIndex ?? '1';

  if (options.clearBackground) {
    // Clear background and shadow on root and all descendants so only the text
    // is visible — matching how labels appear on the canvas.
    [clone, ...Array.from(clone.querySelectorAll<HTMLElement>('*'))].forEach((n) => {
      n.style.background      = 'transparent';
      n.style.backgroundColor = 'transparent';
      n.style.boxShadow       = 'none';
    });
    clone.style.outline       = 'none';
    clone.style.outlineOffset = '0';
  } else {
    // Restore the resting shadow (not the selection-state shadow) and clear
    // the selection outline so the exported snapshot looks unselected.
    clone.style.boxShadow = '2px 6px 16px rgba(0,0,0,0.18)';
    clone.style.outline   = '2px solid transparent';
  }

  container.appendChild(clone);
}

/**
 * Adjust the PngTemplateArt img inside the cloned template so it composites
 * correctly against a transparent background while matching the active theme.
 *
 * Light mode — strip mix-blend-mode (multiply was used to hide the near-white
 *   background against a white canvas; with no background we render normally).
 * Dark mode  — strip mix-blend-mode but KEEP filter:invert(1) so the lines
 *   appear light-coloured, matching what the user sees on the dark canvas.
 */
function normaliseTemplateArt(clone: HTMLElement, isDarkMode: boolean): void {
  const img = clone.querySelector('img');
  if (!img) return;
  img.style.mixBlendMode = 'normal';
  if (!isDarkMode) {
    img.style.filter = 'none';
  }
  // In dark mode, the existing filter:invert(1) is intentionally preserved.
}

/**
 * Export a set of board items as a transparent PNG file and trigger a browser
 * download. When `templateInfo` is provided the export bounding box expands to
 * cover the entire template and the template artwork is included as a bottom
 * layer with normal compositing (no blend-mode tricks).
 *
 * Returns without action when the item lists are empty and no template is given.
 */
export async function exportItemsAsImage(
  stickies: StickyState[],
  labels: TextLabelState[],
  stickyRefs: Map<string, HTMLElement>,
  labelRefs: Map<string, HTMLDivElement>,
  filename: string,
  templateInfo?: TemplateExportInfo,
): Promise<void> {
  // Measure actual rendered dimensions for fit-content labels.
  const labelDims: LabelDomDims = new Map();
  labelRefs.forEach((el, id) => {
    const rect = el.getBoundingClientRect();
    labelDims.set(id, { w: rect.width, h: rect.height });
  });

  const bounds = computeBoundingBox(
    stickies,
    labels,
    labelDims,
    templateInfo?.boardRect,
  );
  if (!bounds) return;

  const container = document.createElement('div');
  // Must be in the viewport (position:fixed at 0,0) for html-to-image to render
  // content correctly. opacity:0 hides it from users during the async capture.
  container.style.cssText = [
    'position:fixed',
    'top:0',
    'left:0',
    `width:${bounds.width}px`,
    `height:${bounds.height}px`,
    'overflow:hidden',
    'pointer-events:none',
    'opacity:0',
    'z-index:99999',
  ].join(';');

  // Template is rendered first (lowest z-order) so stickies appear on top.
  if (templateInfo) {
    appendClone(
      templateInfo.el,
      templateInfo.boardRect.minX,
      templateInfo.boardRect.minY,
      bounds,
      container,
      { zIndex: '0' },
    );
    // The last child is the freshly appended template clone.
    normaliseTemplateArt(container.lastElementChild as HTMLElement, templateInfo.isDarkMode);
  }

  const exportItems: { kind: 'sticky' | 'label'; id: string; x: number; y: number; z: number }[] = [
    ...stickies.map(s => ({ kind: 'sticky' as const, id: s.id, x: s.x, y: s.y, z: s.zIndex ?? 0 })),
    ...labels.map(l => ({ kind: 'label' as const, id: l.id, x: l.x, y: l.y, z: l.zIndex ?? 0 })),
  ].sort((a, b) => a.z - b.z);

  for (const item of exportItems) {
    if (item.kind === 'sticky') {
      const el = stickyRefs.get(item.id);
      if (el) appendClone(el, item.x, item.y, bounds, container, { zIndex: String(item.z) });
    } else {
      const el = labelRefs.get(item.id);
      if (el) appendClone(el, item.x, item.y, bounds, container, { clearBackground: true, zIndex: String(item.z) });
    }
  }

  document.body.appendChild(container);

  try {
    const dataUrl = await toPng(container, {
      // undefined = no background fill → transparent PNG
      backgroundColor: undefined,
      width:           bounds.width,
      height:          bounds.height,
      pixelRatio:      2,
      // The container is hidden with opacity:0; reset it to 1 on the internal
      // clone so the opacity does not bleed into the exported PNG.
      style:           { opacity: '1' },
    });

    const anchor = document.createElement('a');
    anchor.download = filename;
    anchor.href     = dataUrl;
    anchor.click();
  } finally {
    document.body.removeChild(container);
  }
}

/** Generate a timestamped filename for the export PNG. */
export function buildExportFilename(prefix: string): string {
  const now  = new Date();
  const date = now.toISOString().slice(0, 10); // YYYY-MM-DD
  return `${prefix}-${date}.png`;
}
